import { useCallback, useState } from 'react';
import type { CalendarEventDto } from '@chat-emp/shared';

import type { ParticipantId } from '../fixtures/domain.js';
import { draftOf, newDraft, type EventDraft, type Guest } from './draft.js';
import { facesOf } from './invitees.js';

export interface ChatDayState {
  draft: EventDraft;
  /** The covering note, sent beside the invite. Not part of the event. */
  message: string;
  messageOpen: boolean;
  /** Whether the unanswered required fields are marked. See `showGaps`. */
  gapsShown: boolean;
  /** The saved event guests are being added to, or null when booking a new one. */
  editing: CalendarEventDto | null;
  /** Who is already on `editing`: shown as invited, never removable or re-added. */
  invited: { id: string; name: string }[];
}

export interface ChatDayEditor {
  /** Null when the modal is closed — there is no draft between bookings. */
  state: ChatDayState | null;
  /** The "+ Chat Day" button. Opens on a day, at a sensible hour. */
  open: (start: Date) => void;
  /** The pencil on a saved event. Only guests can be added; the draft holds the new ones. */
  openEdit: (event: CalendarEventDto) => void;
  patch: (change: Partial<EventDraft>) => void;
  /** Add guests, ignoring anybody already invited. */
  addGuests: (guests: Guest[]) => void;
  removeGuest: (id: ParticipantId) => void;
  openMessage: () => void;
  dropMessage: () => void;
  setMessage: (message: string) => void;
  showGaps: () => void;
  close: () => void;
}

/**
 * The one Chat Day being written.
 *
 * Its own editor rather than a mode on the event panel, because the two are not
 * two views of one thing: a Chat Day has one surface (a modal over the
 * calendar), many guests where an appointment has exactly one attendee, and a
 * covering message that is not part of the record at all.
 *
 * Also adds guests to a saved event (`openEdit`, AI-3215). There, the draft
 * holds only the new guests; the saved ones stay on `editing`.
 */
export function useChatDay(): ChatDayEditor {
  const [state, setState] = useState<ChatDayState | null>(null);

  const open = useCallback((start: Date) => {
    setState({
      draft: newDraft(start),
      message: '',
      messageOpen: false,
      gapsShown: false,
      editing: null,
      invited: [],
    });
  }, []);

  const openEdit = useCallback((event: CalendarEventDto) => {
    setState({
      draft: draftOf(event),
      message: '',
      messageOpen: false,
      gapsShown: false,
      editing: event,
      invited: facesOf(event.invitees),
    });
  }, []);

  const patch = useCallback((change: Partial<EventDraft>) => {
    setState((current) =>
      current ? { ...current, draft: { ...current.draft, ...change } } : current,
    );
  }, []);

  const addGuests = useCallback((guests: Guest[]) => {
    setState((current) => {
      if (!current) return current;
      // Dedup against who is already coming AND within the batch itself: a list
      // and the chat rows beside it can name the same person, and pressing the
      // list must not put them on twice.
      const seen = new Set<string>([
        ...current.draft.invitees.map((person) => person.id),
        ...current.invited.map((person) => person.id),
      ]);
      const fresh: Guest[] = [];
      for (const guest of guests) {
        if (seen.has(guest.id)) continue;
        seen.add(guest.id);
        fresh.push(guest);
      }
      if (fresh.length === 0) return current;

      return {
        ...current,
        draft: { ...current.draft, invitees: [...current.draft.invitees, ...fresh] },
      };
    });
  }, []);

  const removeGuest = useCallback((id: ParticipantId) => {
    setState((current) =>
      current
        ? {
            ...current,
            draft: {
              ...current.draft,
              invitees: current.draft.invitees.filter((person) => person.id !== id),
            },
          }
        : current,
    );
  }, []);

  const openMessage = useCallback(() => {
    setState((current) => (current ? { ...current, messageOpen: true } : current));
  }, []);

  /**
   * Taking the field off drops what was in it.
   *
   * A message kept behind a closed field is one that gets sent by somebody who
   * removed it, which is worse than losing a sentence they can retype.
   */
  const dropMessage = useCallback(() => {
    setState((current) => (current ? { ...current, messageOpen: false, message: '' } : current));
  }, []);

  const setMessage = useCallback((message: string) => {
    setState((current) => (current ? { ...current, message } : current));
  }, []);

  const showGaps = useCallback(() => {
    setState((current) => (current ? { ...current, gapsShown: true } : current));
  }, []);

  const close = useCallback(() => setState(null), []);

  return {
    state,
    open,
    openEdit,
    patch,
    addGuests,
    removeGuest,
    openMessage,
    dropMessage,
    setMessage,
    showGaps,
    close,
  };
}
